import { useState, useEffect, useCallback } from "react";
import { getAPInventory, uploadAPInventory, updateAPInventoryItem, downloadAPInventory, getAPTagSettings, updateAPTagSettings } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Upload, Package, Download, Search, Edit2, Check, X, Tag, Box, Settings2, Save, Settings } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  { value: "01", label: "January" }, { value: "02", label: "February" }, { value: "03", label: "March" },
  { value: "04", label: "April" }, { value: "05", label: "May" }, { value: "06", label: "June" },
  { value: "07", label: "July" }, { value: "08", label: "August" }, { value: "09", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
];
const YEARS = ["24", "25", "26", "27", "28"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function APInventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [importMonth, setImportMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [importYear, setImportYear] = useState(() => String(new Date().getFullYear()).slice(-2));
  const [showTagSettings, setShowTagSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getAPInventory(); setItems(r.data || []); }
    catch { toast.error("Failed to load AP inventory"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i => i.model_name?.toLowerCase().includes(search.toLowerCase()));
  const totalQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalValue = items.reduce((s, i) => s + (i.quantity || 0) * (i.mrp || 0), 0);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await uploadAPInventory(file); toast.success(`${r.data.new} new, ${r.data.updated} updated`); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try { await updateAPInventoryItem(editItem.sr_no, { quantity: editItem.quantity, mrp: editItem.mrp, unit_price: editItem.unit_price, hsn: editItem.hsn }); toast.success("Updated"); setEditItem(null); await load(); }
    catch { toast.error("Update failed"); }
  };

  const handleDownload = async () => {
    try { const r = await downloadAPInventory(); const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement("a"); a.href = url; a.download = "ap_inventory.xlsx"; a.click(); }
    catch { toast.error("Download failed"); }
  };

  const handlePrintProductTag = (item) => {
    window.open(`${API_BASE}/api/ap/inventory/${item.sr_no}/product-tag`, "_blank");
  };

  const handlePrintBoxTag = () => {
    if (!selectedItem) return;
    const month = `${importMonth}/${importYear}`;
    window.open(`${API_BASE}/api/ap/inventory/${selectedItem.sr_no}/box-tag?import_month=${encodeURIComponent(month)}`, "_blank");
  };

  const getStockStatus = (qty) => {
    if (qty === 0) return <Badge variant="destructive" className="text-[10px]">Out</Badge>;
    if (qty <= 10) return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Low</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">OK</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="ap-inventory-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Package className="w-5 h-5" /> AP Inventory</h1>
          <p className="text-sm text-slate-500 mt-0.5">{items.length} SKUs | {totalQty.toLocaleString()} units | Rs.{(totalValue / 100000).toFixed(1)}L value</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowTagSettings(!showTagSettings)} data-testid="ap-tag-settings-btn">
            <Settings className="w-4 h-4 mr-1.5" />Tag Settings
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-ap-inv"><Download className="w-4 h-4 mr-1.5" />Download</Button>
          <Button size="sm" onClick={() => document.getElementById("ap-inv-upload").click()} disabled={uploading} data-testid="upload-ap-inv">
            <Upload className="w-4 h-4 mr-1.5" />{uploading ? "Uploading..." : "Upload Excel"}
          </Button>
          <input id="ap-inv-upload" type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
        </div>
      </div>

      {showTagSettings && <APTagSettings />}

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4"><p className="text-2xl font-bold text-slate-800">{items.length}</p><p className="text-xs text-slate-500">Total SKUs</p></Card>
        <Card className="p-4"><p className="text-2xl font-bold text-slate-800">{totalQty.toLocaleString()}</p><p className="text-xs text-slate-500">Total Units</p></Card>
        <Card className="p-4"><p className="text-2xl font-bold text-slate-800">Rs.{(totalValue / 100000).toFixed(1)}L</p><p className="text-xs text-slate-500">Stock Value</p></Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="ap-inv-search" />
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm">
            Inventory Items ({filtered.length})
          </CardTitle>
        </CardHeader>
        <ScrollArea className="h-[450px]">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">No inventory items found</p>
            </div>
          ) : (
            <Table className="data-table" data-testid="ap-inventory-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase w-12">#</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-20">Qty</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right w-24">MRP</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-16">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-32">Tags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => {
                  const isEditing = editItem?.sr_no === item.sr_no;
                  return (
                    <TableRow key={item.sr_no}
                      className={`${item.quantity === 0 ? 'bg-red-50' : ''} ${item.quantity > 0 && item.quantity <= 10 ? 'bg-amber-50' : ''}`}>
                      <TableCell className="font-mono text-sm">{item.sr_no}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-medium text-sm">{item.model_name}</p>
                          </div>
                          {!isEditing && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-blue-600 ml-auto" onClick={() => setEditItem({...item})}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {isEditing ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input type="number" className="w-16 h-7 text-center font-mono text-sm" value={editItem.quantity}
                              onChange={e => setEditItem({...editItem, quantity: parseInt(e.target.value) || 0})} />
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-emerald-600" onClick={handleSaveEdit}><Check className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditItem(null)}><X className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <span className="font-mono text-sm">{item.quantity}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        Rs. {item.mrp?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStockStatus(item.quantity)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                            onClick={() => handlePrintProductTag(item)} title="Print Product Tag (38x15mm)">
                            <Tag className="w-3 h-3 mr-1" />38x15
                          </Button>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setImportMonth(String(new Date().getMonth() + 1).padStart(2, "0"));
                                  setImportYear(String(new Date().getFullYear()).slice(-2));
                                }}
                                title="Print Box Tag (50x25mm)">
                                <Box className="w-3 h-3 mr-1" />50x25
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Box Tag Settings</DialogTitle>
                                <DialogDescription>Set the month of import for this product's box tag</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <p className="text-sm font-medium mb-2">{item.model_name}</p>
                                  <p className="text-sm text-slate-500">MRP: Rs. {item.mrp?.toLocaleString()}</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>Month of Import</Label>
                                  <div className="flex gap-2">
                                    <Select value={importMonth} onValueChange={setImportMonth}>
                                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                    <Select value={importYear} onValueChange={setImportYear}>
                                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {YEARS.map(y => <SelectItem key={y} value={y}>20{y}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                <div className="flex gap-2 pt-2">
                                  <Button className="flex-1" onClick={handlePrintBoxTag}>
                                    <Box className="w-4 h-4 mr-2" />Print Tag
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}

function APTagSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getAPTagSettings().then(r => setSettings(r.data)).catch(() => {}); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await updateAPTagSettings(settings); toast.success("Tag settings saved"); } catch { toast.error("Failed"); }
    setSaving(false);
  };

  if (!settings) return null;

  return (
    <Card data-testid="ap-tag-settings">
      <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">Tag Settings (Box Tag)</CardTitle></CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="text-xs font-medium text-slate-600">Imported By Line</label><Input value={settings.imported_by || ""} onChange={e => setSettings({...settings, imported_by: e.target.value})} className="mt-1" data-testid="ap-tag-imported-by" placeholder="Imported by: Vardhman Marketing" /></div>
          <div><label className="text-xs font-medium text-slate-600">Address</label><Input value={settings.address || ""} onChange={e => setSettings({...settings, address: e.target.value})} className="mt-1" placeholder="(H-18, EVEREST BLDG, TARDEO, MUMBAI)" /></div>
          <div><label className="text-xs font-medium text-slate-600">Phone</label><Input value={settings.phone || ""} onChange={e => setSettings({...settings, phone: e.target.value})} className="mt-1" placeholder="+9102231690388" /></div>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="mt-3" data-testid="ap-save-tag-settings"><Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}</Button>
      </CardContent>
    </Card>
  );
}
